import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { 
  FiShoppingBag, 
  FiFolder, 
  FiTool,
  FiDollarSign,
  FiTrendingUp,
  FiDownload,
  FiUpload,
  FiPackage,
  FiDatabase,
  FiFileText,
  FiFile,
  FiUsers,
  FiRefreshCw,
  FiAlertCircle,
  FiCheckCircle
} from 'react-icons/fi';
import DashboardChart from '../../components/Charts/DashboardChart';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import ErrorAlert from '../../components/Common/ErrorAlert';
import ExportImportModal from '../../components/Admin/ExportImportModal';// Only this import
import { productService } from '../../services/productService';
import { categoryService } from '../../services/categoryService';
import { repairService } from '../../services/repairService';
import { formatCurrency } from '../../utils/formatters';
import api from '../../services/api';
import { toast } from 'react-hot-toast';

const Dashboard = () => {
  const [showExportImportModal, setShowExportImportModal] = useState(false);
  const [exportType, setExportType] = useState('all');
  const [stats, setStats] = useState({
    totalRevenue: 0,
    averagePrice: 0,
    totalProducts: 0,
    totalCategories: 0,
    totalRepairs: 0,
    totalUsers: 0
  });

  // Fetch data with React Query
  const { 
    data: products, 
    isLoading: productsLoading, 
    error: productsError,
    refetch: refetchProducts 
  } = useQuery(
    'products',
    () => productService.getAll({ limit: 1000 }),
    {
      retry: 1,
      onSuccess: (data) => {
        calculateStats(data);
      }
    }
  );

  const { 
    data: categories, 
    isLoading: categoriesLoading, 
    error: categoriesError,
    refetch: refetchCategories 
  } = useQuery(
    'categories',
    categoryService.getAll,
    {
      retry: 1
    }
  );

  const { 
    data: repairs, 
    isLoading: repairsLoading, 
    error: repairsError,
    refetch: refetchRepairs 
  } = useQuery(
    'repairs',
    () => repairService.getAll({ limit: 1000 }),
    {
      retry: 1
    }
  );

  const isLoading = productsLoading || categoriesLoading || repairsLoading;

  // Calculate statistics
  const calculateStats = (productsData) => {
    if (!productsData) return;
    
    const totalRevenue = productsData.reduce((sum, product) => 
      sum + (product.price * (1 - (product.discount_percent || 0) / 100)), 0);
    
    const averagePrice = productsData.length > 0 
      ? totalRevenue / productsData.length 
      : 0;
    
    setStats({
      totalRevenue,
      averagePrice,
      totalProducts: productsData.length,
      totalCategories: categories?.length || 0,
      totalRepairs: repairs?.length || 0,
      totalUsers: 0
    });
  };

  // Update stats when data changes
  useEffect(() => {
    if (products) {
      calculateStats(products);
    }
  }, [products, categories, repairs]);

  // Quick stats cards
  const statCards = [
    {
      title: 'ផលិតផលសរុប',
      titleEn: 'Total Products',
      value: stats.totalProducts,
      icon: FiShoppingBag,
      color: 'bg-gradient-to-r from-red-500 to-red-600',
      change: '+12%',
      exportType: 'products',
      bgColor: 'bg-red-50'
    },
    {
      title: 'ប្រភេទ',
      titleEn: 'Categories',
      value: stats.totalCategories,
      icon: FiFolder,
      color: 'bg-gradient-to-r from-blue-500 to-blue-600',
      change: '+5%',
      exportType: 'categories',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'អត្ថបទ',
      titleEn: 'Repair Articles',
      value: stats.totalRepairs,
      icon: FiTool,
      color: 'bg-gradient-to-r from-green-500 to-green-600',
      change: '+8%',
      exportType: 'repairs',
      bgColor: 'bg-green-50'
    }
  ];

  const handleExportClick = (type) => {
    setExportType(type);
    setShowExportImportModal(true);
  };

  const handleRefreshAll = async () => {
    toast.loading('Refreshing data...', { id: 'refresh' });
    await Promise.all([
      refetchProducts(),
      refetchCategories(),
      refetchRepairs()
    ]);
    toast.success('Data refreshed successfully!', { id: 'refresh' });
  };

  // Get recent items (last 5)
  const recentProducts = products?.slice(0, 5) || [];
  const recentRepairs = repairs?.slice(0, 4) || [];

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">ផ្ទាំងគ្រប់គ្រង</h1>
            <p className="text-sm text-gray-500 mt-1">ស្វាគមន៍មកកាន់ប្រព័ន្ធគ្រប់គ្រង GrandFatherSmile</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleRefreshAll}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              title="Refresh all data"
            >
              <FiRefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={() => setShowExportImportModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <FiUpload className="w-4 h-4" />
              <span>Import/Export</span>
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {statCards.map((stat, index) => (
            <div 
              key={index} 
              className={`${stat.bgColor} rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden`}
            >
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`${stat.color} p-3 rounded-lg`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                      <p className="text-2xl font-bold text-gray-900">{stat.value.toLocaleString()}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleExportClick(stat.exportType)}
                    className="p-2 text-gray-400 hover:text-blue-600 transition-colors rounded-full hover:bg-white"
                    title={`Export ${stat.titleEn}`}
                  >
                    <FiDownload className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-4 flex items-center text-sm">
                  <FiTrendingUp className="w-4 h-4 text-green-500 mr-1" />
                  <span className="text-green-500 font-medium">{stat.change}</span>
                  <span className="text-gray-500 ml-2">ពីខែមុន</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Revenue and Average Price */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-xl text-white p-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold opacity-90">ចំណូលសរុប</h3>
                  <p className="text-sm opacity-75">Total Revenue</p>
                </div>
                <FiDollarSign className="w-8 h-8 opacity-75" />
              </div>
              <p className="text-3xl font-bold">{formatCurrency(stats.totalRevenue)}</p>
              <p className="text-sm opacity-75 mt-2">ពីផលិតផលទាំងអស់</p>
              
              <div className="mt-4 pt-4 border-t border-red-500 border-opacity-30">
                <div className="flex justify-between items-center">
                  <span className="text-sm opacity-75">តម្លៃមធ្យម</span>
                  <span className="text-lg font-semibold">{formatCurrency(stats.averagePrice)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">ស្ថិតិផលិតផលតាមប្រភេទ</h3>
              <DashboardChart products={products} />
            </div>
          </div>
        </div>

        {/* Recent Products Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="flex justify-between items-center p-6 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">ផលិតផលថ្មីៗ</h3>
              <p className="text-sm text-gray-500 mt-1">បង្ហាញផលិតផល ៥ ចុងក្រោយបំផុត</p>
            </div>
            <button
              onClick={() => handleExportClick('products')}
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              <FiDownload className="w-4 h-4" />
              នាំចេញទាំងអស់
            </button>
          </div>
          
          {recentProducts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ផលិតផល</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ប្រភេទ</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">តម្លៃ</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">បញ្ចុះតម្លៃ</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">តម្លៃពិត</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ស្ថានភាព</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentProducts.map((product) => {
                    const finalPrice = product.price * (1 - (product.discount_percent || 0) / 100);
                    return (
                      <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {product.images?.[0] && (
                              <img
                                src={`http://localhost:8000/${product.images[0].filepath}`}
                                alt={product.title}
                                className="w-10 h-10 rounded-lg object-cover mr-3"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "";
                                }}
                              />
                            )}
                            <div>
                              <p className="font-medium text-gray-900">{product.title}</p>
                              <p className="text-xs text-gray-500">{product.description?.substring(0, 50)}...</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                            {product.category?.name || 'គ្មានប្រភេទ'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-900 font-medium">
                          {formatCurrency(product.price)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {product.discount_percent > 0 ? (
                            <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-600 rounded-full">
                              {product.discount_percent}% OFF
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-green-600 font-semibold">
                          {formatCurrency(finalPrice)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="flex items-center">
                            <FiCheckCircle className="w-4 h-4 text-green-500 mr-1" />
                            <span className="text-sm text-green-600">កំពុងដំណើរការ</span>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <FiShoppingBag className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">មិនមានផលិតផលទេ</p>
            </div>
          )}
        </div>
      </div>

      {/* Export/Import Modal */}
      {showExportImportModal && (
        <ExportImportModal
          isOpen={showExportImportModal}
          onClose={() => {
            setShowExportImportModal(false);
            setExportType('all');
          }}
          type={exportType}
        />
      )}
    </div>
  );
};

export default Dashboard;
